'use client'

import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'

import Link from 'next/link'

import EventDates from '@/components/event-dates/EventDates'

import { LinkProps, EventDatesProps } from '@/types/types'
import { decodeSafeEntities } from '@/lib/sanitise-strings'

export default function EventsSubHeader({
  subHeaderData,
}: {
  subHeaderData: {
    title: string
    permalink: string
    dates?: EventDatesProps
    links: { link: LinkProps }[]
    button?: LinkProps[]
  }
}) {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (document.getElementById('sub-header')) {
      setPortalTarget(document.getElementById('sub-header'))
    }
  }, [])

  if (!portalTarget) {
    return null
  }

  const {
    title,
    permalink,
    dates,
    links = [],
    button: { url: buttonUrl, title: buttonText, target: buttonTarget } = {} as LinkProps,
  } = subHeaderData

  if (links.length === 0) {
    return null
  }

  const eventDates = dates ? (
    <div className="p--xsmall mb-0 leading-[120%]">
      <EventDates dates={dates} />
    </div>
  ) : null

  const navItemClass = `
    flex
    py-xxxs
    items-center
    text-center

    whitespace-nowrap
    
    border-y-2
    border-transparent
    l-up:relative
    l-up:hover:border-b-black
  `

  const renderSubHeader = (
    <div className="overflow-x-auto border-t bg-theme py-xs text-fg theme--primary-50 l:py-xxs">
      <div className="container container--l">
        <div className="flex w-full items-center justify-between gap-x-20">
          <div className="min-w-1/4 shrink whitespace-nowrap l:hidden">
            <Link
              href={permalink}
              className="mb-0 overflow-hidden text-ellipsis text-[length:--p-size] font-bold">
              {decodeSafeEntities(title)}
            </Link>
            {eventDates}
          </div>

          {links.length > 0 ? (
            <nav className="flex grow items-center l-up:justify-center">
              <ul className="flex items-center gap-s ms-up:gap-m l-up:gap-l">
                {links.map((link, index) => {
                  const { link: { url: linkUrl, title: linkText, target: linkTarget } = {} } = link

                  if (!linkUrl || !linkText) {
                    return null
                  }

                  return (
                    <li key={index} className={navItemClass}>
                      <Link href={linkUrl} target={linkTarget ?? '_self'}>
                        {linkText}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </nav>
          ) : null}

          <div className="min-w-1/4 shrink-0 text-right">
            {buttonUrl && buttonText ? (
              <Link
                href={buttonUrl}
                className="btn btn--outline btn--small s:btn--xsmall whitespace-nowrap"
                target={buttonTarget ?? '_self'}>
                {buttonText}
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )

  return <>{createPortal(renderSubHeader, portalTarget)}</>
}
